import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  FaClipboardList, FaSearch, FaSpinner, FaHourglassHalf, FaCheckCircle,
  FaPlus, FaUserPlus, FaMapMarkerAlt, FaClock, FaExclamationTriangle
} from 'react-icons/fa';
import AdminLayout from '../layouts/AdminLayout';
import { quotes, clients } from '../api';
import LocationPicker from '../components/LocationPicker';
import { useGoogleMaps } from '../hooks/useGoogleMaps';
import {
  Chart as ChartJS,
  CategoryScale, LinearScale, BarElement, ArcElement, Tooltip, Legend
} from 'chart.js';
import { Bar, Doughnut } from 'react-chartjs-2';

ChartJS.register(CategoryScale, LinearScale, BarElement, ArcElement, Tooltip, Legend);

const STATUS_LABELS = {
  quote_request: 'Quote Request',
  draft: 'Draft',
  sent: 'Sent',
  approved: 'Approved',
  rejected: 'Rejected',
  cancelled: 'Cancelled',
  client_accepted: 'Client Accepted',
  // A voided quote is one whose invoice was cancelled before any payment was received. It must read
  // "Voided", never "Credited" — nothing was credited because nothing was ever paid.
  void: 'Voided'
};

const STATUS_COLORS = {
  quote_request: 'bg-amber-100 text-amber-700',
  draft: 'bg-gray-100 text-gray-600',
  sent: 'bg-blue-100 text-blue-700',
  approved: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700',
  cancelled: 'bg-gray-100 text-gray-500',
  client_accepted: 'bg-emerald-100 text-emerald-700',
  void: 'bg-gray-200 text-gray-700'
};

const TYPES = ['abnormal', 'minerals', 'agri', 'general'];

const HOUR = 3600000;

const formatDuration = (ms) => {
  if (ms == null) return '—';
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return '< 1m';
  const hours = Math.floor(mins / 60);
  const remMins = mins % 60;
  if (hours < 24) return `${hours}h ${remMins}m`;
  const days = Math.floor(hours / 24);
  const remHours = hours % 24;
  return `${days}d ${remHours}h ${remMins}m`;
};

const elapsedColor = (r) => {
  if (!r.pending) return 'text-green-600';
  if (r.elapsed_ms < 6 * HOUR) return 'text-green-600';
  if (r.elapsed_ms < 24 * HOUR) return 'text-yellow-600';
  if (r.elapsed_ms < 48 * HOUR) return 'text-red-600';
  return 'text-red-800';
};

function QuoteRegisterPage({ onLogout }) {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  useGoogleMaps();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({});
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [clientsList, setClientsList] = useState([]);

  const emptyForm = {
    customerChoice: '', quote_type: 'abnormal', description: '',
    origin: '', destination: '',
    customer_name: '', contact_person: '', contact_phone: '', contact_email: '',
    customer_vat: '', customer_address: ''
  };
  const [form, setForm] = useState(emptyForm);

  const loadRegister = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await quotes.getRegister();
      if (res.success) { setRows(res.rows || []); setStats(res.stats || {}); }
      else if (!silent) toast.error(res.error || 'Failed to load register');
    } catch (err) {
      console.error(err);
      if (!silent) toast.error('Failed to load quote register');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => { loadRegister(); loadClients(); }, []);
  useEffect(() => { const t = setInterval(() => loadRegister(true), 60000); return () => clearInterval(t); }, []);

  const loadClients = async () => {
    try { const r = await clients.getAll(); if (r.success) setClientsList(r.clients || []); } catch {}
  };

  const updateForm = (patch) => setForm((f) => ({ ...f, ...patch }));

  const handleSubmitRequest = async (e) => {
    e.preventDefault();
    if (!form.description || !form.description.trim()) { toast.error('Please include the client request'); return; }
    if (!form.customerChoice) { toast.error('Select a client or add a new one'); return; }
    setSaving(true);
    try {
      const payload = {
        quote_type: form.quote_type,
        description: form.description.trim(),
        origin: form.origin || null,
        destination: form.destination || null,
        created_by: user.id || null
      };
      if (form.customerChoice === 'new') {
        if (!form.customer_name || !form.customer_name.trim()) { toast.error('Enter the new client company name'); setSaving(false); return; }
        payload.customer_name = form.customer_name.trim();
        payload.contact_person = form.contact_person || null;
        payload.contact_phone = form.contact_phone || null;
        payload.contact_email = form.contact_email || null;
        payload.customer_vat = form.customer_vat || null;
        payload.customer_address = form.customer_address || null;
      } else {
        payload.customer_id = form.customerChoice;
      }
      const res = await quotes.createRequest(payload);
      if (res.success) {
        toast.success(`Quote request ${res.quote?.quote_number} created`);
        setShowRequestModal(false);
        setForm({ ...emptyForm });
        loadRegister();
      } else {
        toast.error(res.error || 'Failed to create request');
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to create request');
    } finally { setSaving(false); }
  };

  // ---- Chart data ----
  const requestsPerDay = stats.requests_per_day || [];
  const maxDay = requestsPerDay.length ? Math.max(...requestsPerDay.map((d) => d.count)) : 0;
  const requestsPerDayChart = {
    labels: requestsPerDay.map((d) => d.label),
    datasets: [{
      label: 'Requests',
      data: requestsPerDay.map((d) => d.count),
      backgroundColor: requestsPerDay.map((d) => (d.count === maxDay && maxDay > 0 ? '#00A86B' : '#CBD5E1')),
      borderRadius: 6
    }]
  };

  const statusCounts = stats.status_counts || {};
  const statusKeys = Object.keys(statusCounts);
  const statusChart = {
    labels: statusKeys.map((s) => STATUS_LABELS[s] || s),
    datasets: [{
      data: statusKeys.map((s) => statusCounts[s]),
      backgroundColor: ['#F59E0B', '#94A3B8', '#3B82F6', '#10B981', '#EF4444', '#6B7280', '#059669'],
      borderWidth: 2,
      borderColor: '#FFFFFF'
    }]
  };

  const buckets = stats.buckets || { lt6h: 0, '6_24h': 0, '24_48h': 0, gt48h: 0 };
  const bucketChart = {
    labels: ['< 6h', '6–24h', '24–48h', '> 48h'],
    datasets: [{
      label: 'Quotes',
      data: [buckets.lt6h, buckets['6_24h'], buckets['24_48h'], buckets.gt48h],
      backgroundColor: ['#10B981', '#F59E0B', '#EF4444', '#7F1D1D'],
      borderRadius: 6
    }]
  };

  const barOptions = {
    responsive: true, maintainAspectRatio: false,
    plugins: { legend: { display: false }, tooltip: { enabled: true } },
    scales: { y: { beginAtZero: true, ticks: { precision: 0 } } }
  };
  const donutOptions = { responsive: true, maintainAspectRatio: false, plugins: { legend: { position: 'bottom' } } };

  const filtered = rows.filter((r) => {
    if (filterStatus !== 'all' && r.status !== filterStatus) return false;
    if (search) {
      const s = search.toLowerCase();
      const hay = `${r.quote_number || ''} ${r.customer_name || ''} ${r.contact_person || ''} ${r.description || ''}`.toLowerCase();
      if (!hay.includes(s)) return false;
    }
    return true;
  });

  const avgHrs = stats.avg_turnaround_hours;
  const kpis = [
    { label: 'Total Requests', value: stats.total || 0, color: 'text-navy', icon: <FaClipboardList /> },
    { label: 'Pending', value: stats.pending || 0, color: 'text-amber-600', icon: <FaHourglassHalf /> },
    { label: 'Avg Turnaround', value: avgHrs != null ? `${avgHrs.toFixed(1)} hrs` : '—', color: 'text-green', icon: <FaClock /> },
    { label: 'Overdue (>48h)', value: stats.overdue || 0, color: 'text-red-700', icon: <FaExclamationTriangle /> }
  ];

  return (
    <AdminLayout activeTab="quoteregister" onLogout={onLogout} user={user}>
      <div className="space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-navy flex items-center gap-2">
              <FaClipboardList className="text-green" /> Quote Request Register
            </h1>
            <p className="text-sm text-gray-500">Request received → rate gathered → quotation sent. Timer stops when the quote is sent.</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => navigate('/quotes')}
              className="px-4 py-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Quotations
            </button>
            <button
              onClick={() => setShowRequestModal(true)}
              className="px-4 py-2 bg-navy text-white rounded-lg font-medium hover:bg-green hover:text-navy transition-colors flex items-center gap-2"
            >
              <FaPlus size={14} /> New Request
            </button>
          </div>
        </div>

        {/* KPI cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {kpis.map((k) => (
            <div key={k.label} className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex items-center gap-2 text-xs text-gray-500 uppercase tracking-wide">
                <span className="text-green">{k.icon}</span> {k.label}
              </div>
              <div className={`text-2xl font-bold mt-1 ${k.color}`}>{k.value}</div>
            </div>
          ))}
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <h3 className="text-sm font-semibold text-navy mb-3">Requests per Day (last 7 days)</h3>
            <div style={{ height: '200px' }}>
              {requestsPerDay.length ? <Bar data={requestsPerDayChart} options={barOptions} /> : <p className="text-sm text-gray-400">No data</p>}
            </div>
            {maxDay > 0 && <p className="text-xs text-gray-400 mt-2">🔆 Busiest day highlighted in green</p>}
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <h3 className="text-sm font-semibold text-navy mb-3">Status Breakdown</h3>
            <div style={{ height: '200px' }}>
              {statusKeys.length ? <Doughnut data={statusChart} options={donutOptions} /> : <p className="text-sm text-gray-400">No data</p>}
            </div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <h3 className="text-sm font-semibold text-navy mb-3">Turnaround Time (request → sent)</h3>
            <div style={{ height: '200px' }}>
              <Bar data={bucketChart} options={barOptions} />
            </div>
            <p className="text-xs text-gray-400 mt-2">Coloured to match the timer SLA</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[220px]">
            <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search quote number, customer, contact, description..."
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green focus:ring-2 focus:ring-green/10"
            />
          </div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
          >
            <option value="all">All statuses</option>
            {Object.keys(STATUS_LABELS).map((s) => (
              <option key={s} value={s}>{STATUS_LABELS[s]}</option>
            ))}
          </select>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-16"><FaSpinner className="animate-spin text-green text-3xl" /></div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16 text-gray-400">No quote requests match your filters.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 text-left text-xs text-gray-500 uppercase tracking-wide">
                    <th className="px-4 py-3">Quote #</th>
                    <th className="px-4 py-3">Customer</th>
                    <th className="px-4 py-3">Type</th>
                    <th className="px-4 py-3">Route</th>
                    <th className="px-4 py-3">Requested</th>
                    <th className="px-4 py-3">Elapsed</th>
                    <th className="px-4 py-3">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r) => (
                    <tr key={r.id} onClick={() => navigate(`/quotes/requests/${r.id}`)} className="border-t border-gray-100 hover:bg-green/5 cursor-pointer">
                      <td className="px-4 py-3 font-semibold text-navy">{r.quote_number}</td>
                      <td className="px-4 py-3">
                        <div className="font-medium">{r.customer_name || '—'}</div>
                        {r.contact_person && <div className="text-xs text-gray-400">{r.contact_person}</div>}
                      </td>
                      <td className="px-4 py-3 capitalize">{r.quote_type}</td>
                      <td className="px-4 py-3 text-gray-600">
                        {r.origin ? <span className="inline-flex items-center gap-1"><FaMapMarkerAlt className="text-green text-xs" /> {r.origin}</span> : null}
                        {r.origin && r.destination ? <span className="text-gray-400"> → </span> : null}
                        {r.destination ? <span className="inline-flex items-center gap-1"><FaMapMarkerAlt className="text-red-400 text-xs" /> {r.destination}</span> : null}
                        {!r.origin && !r.destination ? '—' : null}
                      </td>
                      <td className="px-4 py-3">
                        <div>{r.requested_at ? new Date(r.requested_at).toLocaleString('en-ZA', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—'}</div>
                        {r.sent_at && <div className="text-xs text-gray-400">sent {new Date(r.sent_at).toLocaleString('en-ZA', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</div>}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 font-semibold ${elapsedColor(r)}`}>
                          {r.pending ? <FaHourglassHalf /> : <FaCheckCircle />}
                          {formatDuration(r.elapsed_ms)}
                          {r.pending && r.elapsed_ms > 48 * HOUR && <FaExclamationTriangle className="text-red-700" />}
                        </span>
                        {r.pending && <div className="text-xs text-gray-400">still pending</div>}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${STATUS_COLORS[r.status] || 'bg-gray-100 text-gray-600'}`}>
                          {STATUS_LABELS[r.status] || r.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* New Request modal */}
        {showRequestModal && (
          <div className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center p-4 overflow-y-auto">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl mt-8 mb-8">
              <div className="flex items-center justify-between p-5 border-b border-gray-100">
                <h2 className="text-lg font-bold text-navy">New Quote Request</h2>
                <button type="button" onClick={() => setShowRequestModal(false)} className="text-gray-400 hover:text-gray-600 text-2xl leading-none">&times;</button>
              </div>
              <form onSubmit={handleSubmitRequest} className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Client</label>
                  <select
                    value={form.customerChoice}
                    onChange={(e) => updateForm({ customerChoice: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                  >
                    <option value="">Select a client…</option>
                    {clientsList.map((c) => (
                      <option key={c.id} value={c.id}>{c.company_name}</option>
                    ))}
                    <option value="new">＋ New Client (add details below)</option>
                  </select>
                </div>

                {form.customerChoice === 'new' && (
                  <div className="p-4 rounded-xl bg-blue-50/50 border border-blue-100 space-y-3">
                    <p className="text-xs text-blue-600 font-medium flex items-center gap-1"><FaUserPlus /> New client details</p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div className="md:col-span-2">
                        <label className="block text-xs text-gray-600 mb-1">Company Name *</label>
                        <input value={form.customer_name} onChange={(e) => updateForm({ customer_name: e.target.value })} className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green" placeholder="Acme Logistics" />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Contact Person</label>
                        <input value={form.contact_person} onChange={(e) => updateForm({ contact_person: e.target.value })} className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green" />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Phone</label>
                        <input value={form.contact_phone} onChange={(e) => updateForm({ contact_phone: e.target.value })} className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green" />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">Email</label>
                        <input value={form.contact_email} onChange={(e) => updateForm({ contact_email: e.target.value })} className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green" />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-600 mb-1">VAT Number</label>
                        <input value={form.customer_vat} onChange={(e) => updateForm({ customer_vat: e.target.value })} className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Physical Address</label>
                      <input value={form.customer_address} onChange={(e) => updateForm({ customer_address: e.target.value })} className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green" />
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Quote Type</label>
                  <select
                    value={form.quote_type}
                    onChange={(e) => updateForm({ quote_type: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                  >
                    {TYPES.map((t) => <option key={t} value={t} className="capitalize">{t}</option>)}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Client Request Details</label>
                  <textarea
                    value={form.description}
                    onChange={(e) => updateForm({ description: e.target.value })}
                    rows={4}
                    placeholder="Paste the client's request here…"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-green"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Origin</label>
                    <LocationPicker label="Origin" value={form.origin} onChange={(v) => updateForm({ origin: v })} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Destination</label>
                    <LocationPicker label="Destination" value={form.destination} onChange={(v) => updateForm({ destination: v })} />
                  </div>
                </div>

                <div className="flex items-center justify-end gap-3 pt-2">
                  <button type="button" onClick={() => setShowRequestModal(false)} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">Cancel</button>
                  <button type="submit" disabled={saving} className="px-5 py-2 bg-navy text-white rounded-lg font-medium hover:bg-green hover:text-navy transition-colors disabled:opacity-50 flex items-center gap-2">
                    {saving ? <FaSpinner className="animate-spin" /> : <FaPlus size={14} />} Save Request
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}

export default QuoteRegisterPage;

